import { useRef, useState, useCallback, useEffect } from 'react';

// Text-to-speech using the Web Speech API (no server cost).
// Supported: Chrome, Safari 7+, Firefox, Edge. iOS Safari supported.
export function useTTS() {
  const supported = typeof window !== 'undefined' && 'speechSynthesis' in window;

  const speak = useCallback((text) => {
    if (!supported || !text) return;
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    utt.rate = 1.0;
    utt.pitch = 1.0;
    window.speechSynthesis.speak(utt);
  }, [supported]);

  const stop = useCallback(() => {
    if (supported) window.speechSynthesis.cancel();
  }, [supported]);

  // iOS pauses synthesis after ~15s; this keepalive prevents the silent-cut bug.
  useEffect(() => {
    if (!supported) return;
    const interval = setInterval(() => {
      if (window.speechSynthesis.speaking) {
        window.speechSynthesis.pause();
        window.speechSynthesis.resume();
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [supported]);

  return { speak, stop, supported };
}

// Speech-to-text using the Web Speech Recognition API.
// Supported: Chrome, Edge, Android WebView. NOT available on iOS Safari.
// Callers should check `supported` and fall back to a text input on iOS.
export function useSTT() {
  const [transcript, setTranscript] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState(null);
  const recRef = useRef(null);

  const SR = typeof window !== 'undefined'
    && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const supported = Boolean(SR);

  const start = useCallback(() => {
    if (!SR) return;
    const rec = new SR();
    recRef.current = rec;
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = 'en-US';
    rec.onresult = (e) => {
      const t = e.results[0]?.[0]?.transcript || '';
      setTranscript(t);
      setIsListening(false);
    };
    rec.onerror = (e) => {
      setError(e.error === 'not-allowed' ? 'Microphone access denied' : e.error);
      setIsListening(false);
    };
    rec.onend = () => setIsListening(false);
    setTranscript('');
    setError(null);
    setIsListening(true);
    rec.start();
  }, [SR]);

  const stop = useCallback(() => {
    recRef.current?.stop();
    setIsListening(false);
  }, []);

  const reset = useCallback(() => {
    setTranscript('');
    setError(null);
  }, []);

  return { transcript, setTranscript, isListening, start, stop, reset, error, supported };
}
